/**
 * Direct-dispatch channel for inbound GAME_EVENT messages.
 *
 * Why not a single last-message slot: two GAME_EVENT postMessages can land back-to-back;
 * a keep-the-latest slot silently drops the earlier event. Instead the router calls a
 * dispatch callback DIRECTLY (one call per event), so every event is delivered in order.
 * The consumer (the standalone entry's game-event forwarder) registers its dispatch via
 * `register()`; events that arrive before registration are queued and flushed on register.
 */

/** The router-side payload: a postMessage-shaped object whose `.data` carries the envelope. */
export type GameEventMessage = { data?: unknown };

export type GameEventConsumer = (event: GameEventMessage) => void;

export interface GameEventDispatcher {
  /** Deliver an event now (if a consumer is registered) or queue it until one registers. */
  dispatch: GameEventConsumer;
  /** Register the consumer; flushes any events queued before registration. Returns an unregister fn. */
  register: (consumer: GameEventConsumer) => () => void;
}

export function createGameEventDispatcher(): GameEventDispatcher {
  let consumer: GameEventConsumer | null = null;
  const pending: GameEventMessage[] = [];

  return {
    dispatch: (event) => {
      if (consumer) {
        consumer(event);
      } else {
        // No consumer yet (widget not mounted): queue, never overwrite.
        pending.push(event);
      }
    },
    register: (next) => {
      consumer = next;
      // Flush in arrival order so no early event is lost.
      while (pending.length > 0) {
        consumer(pending.shift() as GameEventMessage);
      }
      return () => {
        if (consumer === next) {
          consumer = null;
        }
      };
    },
  };
}
